import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Building2, Users, ArrowLeft, Phone, MapPin, Calendar,
  Contact, Briefcase, Home, CheckSquare, TrendingUp,
  UserPlus, Mail, Shield, Settings, ChevronDown, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Stat Card Component
const StatCard = ({ title, value, icon: Icon, color = 'blue', subtitle }) => {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-700',
    green: 'bg-green-100 text-green-700',
    purple: 'bg-purple-100 text-purple-700',
    amber: 'bg-amber-100 text-amber-700',
    rose: 'bg-rose-100 text-rose-700',
    slate: 'bg-slate-100 text-slate-700'
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-500">{title}</p>
            <p className="text-2xl font-bold text-slate-900">{value}</p>
            {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
          </div>
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>
            <Icon className="h-6 w-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Role Badge Colors
const roleBadgeColors = {
  super_admin: 'bg-red-500',
  account_owner: 'bg-amber-500',
  admin: 'bg-purple-500',
  sales_manager: 'bg-blue-500',
  sales_rep: 'bg-green-500',
  viewer: 'bg-slate-500'
};

const roleLabels = {
  super_admin: 'Super Admin',
  account_owner: 'Account Owner',
  admin: 'Admin',
  sales_manager: 'Sales Manager',
  sales_rep: 'Sales Agent',
  viewer: 'Viewer'
};

// Mirror the Team Management page's badge vocabulary so the embedded
// Sales Teams list here shows the SAME team type per row (General,
// Telesales, Sales Agents, AI Agents) instead of a hardcoded label.
const teamTypeLabels = {
  ai_agents: 'AI Agents',
  sales_agents: 'Sales Agents',
  telesales: 'Telesales',
  general: 'General',
};
const teamTypeColors = {
  ai_agents: 'text-violet-600 border-violet-200',
  sales_agents: 'text-green-600 border-green-200',
  telesales: 'text-blue-600 border-blue-200',
  general: 'text-slate-600 border-slate-200',
};

const OrganizationDashboard = () => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [organization, setOrganization] = useState(null);
  const [users, setUsers] = useState([]);
  const [metrics, setMetrics] = useState({});
  const [teams, setTeams] = useState([]);
  const [expandedTeams, setExpandedTeams] = useState({});
  // "Add Agent to Team" state: which team we're adding to + which user was picked
  const [addAgentTeam, setAddAgentTeam] = useState(null);
  const [addAgentUserId, setAddAgentUserId] = useState('');
  const [savingAgent, setSavingAgent] = useState(false);

  const fetchTeams = async () => {
    try {
      const res = await axios.get(
        `${API_URL}/api/teams/list?token=${token}&organization_id=${orgId}`
      );
      setTeams(res.data?.teams || []);
    } catch (e) {
      console.error('Failed to fetch teams:', e);
      setTeams([]);
    }
  };

  const toggleTeam = (teamId) => {
    setExpandedTeams((prev) => ({ ...prev, [teamId]: !prev[teamId] }));
  };

  const handleDeleteTeam = async (teamId, teamName) => {
    if (!window.confirm(`Delete team "${teamName}"? Its agents become unassigned.`)) return;
    try {
      const res = await axios.delete(`${API_URL}/api/teams/${teamId}?token=${token}`);
      if (res.data?.success) {
        toast.success('Team deleted');
        fetchTeams();
      } else {
        toast.error(res.data?.error || 'Failed to delete team');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete team');
    }
  };

  const getUsersForTeam = (teamId) =>
    Array.isArray(users) ? users.filter((u) => u.team_id === teamId) : [];

  // Anyone in the org who is NOT already on the target team. Includes
  // unassigned users AND users currently on OTHER teams — the backend
  // treats an add as a transfer, which is the same UX the Organization
  // Structure page has.
  const getAssignableUsers = (teamId) =>
    Array.isArray(users)
      ? users.filter((u) => u.team_id !== teamId && !u.is_account_owner && u.role !== 'account_owner')
      : [];

  const openAddAgentDialog = (team, e) => {
    if (e) e.stopPropagation();
    setAddAgentTeam(team);
    setAddAgentUserId('');
  };

  const handleAddAgentToTeam = async () => {
    if (!addAgentTeam || !addAgentUserId) {
      toast.error('Pick a user to add');
      return;
    }
    setSavingAgent(true);
    try {
      const picked = users.find((u) => u.id === addAgentUserId);
      const res = await axios.post(
        `${API_URL}/api/teams/${addAgentTeam.id}/members?token=${token}`,
        { user_id: addAgentUserId, role: picked?.role || 'sales_rep' }
      );
      if (res.data?.success !== false) {
        toast.success(`Added ${picked?.full_name || 'agent'} to ${addAgentTeam.name}`);
        setAddAgentTeam(null);
        setAddAgentUserId('');
        // Refresh both — user's team_id changed AND team member_count changed.
        fetchDashboardData();
        fetchTeams();
      } else {
        toast.error(res.data?.error || 'Failed to add agent');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add agent');
    } finally {
      setSavingAgent(false);
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/organizations/${orgId}/dashboard?token=${token}`);
      const data = await response.json();
      
      if (data.success) {
        setOrganization(data.organization);
        setUsers(data.users || []);
        setMetrics(data.metrics || {});
      } else {
        toast.error(data.error || data.detail || 'Failed to load organization data');
        navigate('/organizations');
      }
    } catch (error) {
      console.error('Failed to fetch organization dashboard:', error);
      toast.error('Failed to load organization data');
      navigate('/organizations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (orgId) {
      fetchDashboardData();
      fetchTeams();
    }
  }, [orgId, token]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      timeZone: 'Asia/Dubai',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-slate-500">Loading organization data...</p>
        </div>
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="p-6">
        <Card className="p-8 text-center">
          <Building2 className="h-12 w-12 mx-auto text-slate-400" />
          <h3 className="text-lg font-medium text-slate-800 mt-4">Organization Not Found</h3>
          <p className="text-slate-500 mt-2">The organization you're looking for doesn't exist.</p>
          <Button onClick={() => navigate('/organizations')} className="mt-4">
            Back to Organizations
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="organization-dashboard-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => navigate('/organizations')}
          className="gap-2"
        >
          <ArrowLeft size={16} />
          Back
        </Button>
        
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-xl bg-blue-100 text-blue-700 flex items-center justify-center text-2xl font-bold">
              {organization.name?.[0]?.toUpperCase() || 'O'}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">{organization.name}</h1>
              <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                {organization.address && (
                  <span className="flex items-center gap-1">
                    <MapPin size={14} />
                    {organization.address}
                  </span>
                )}
                {organization.phone && (
                  <span className="flex items-center gap-1">
                    <Phone size={14} />
                    {organization.phone}
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <Calendar size={14} />
                  Created {formatDate(organization.created_at)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard 
          title="Total Users" 
          value={metrics.total_users || 0} 
          icon={Users} 
          color="blue"
        />
        <StatCard 
          title="Contacts" 
          value={metrics.total_contacts || 0} 
          icon={Contact} 
          color="green"
          subtitle={`+${metrics.recent_contacts_30d || 0} last 30 days`}
        />
        <StatCard 
          title="Deals" 
          value={metrics.total_deals || 0} 
          icon={Briefcase} 
          color="purple"
          subtitle={`+${metrics.recent_deals_30d || 0} last 30 days`}
        />
        <StatCard 
          title="Properties" 
          value={metrics.total_properties || 0} 
          icon={Home} 
          color="amber"
        />
      </div>

      {/* Secondary Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <StatCard 
          title="Tasks" 
          value={metrics.total_tasks || 0} 
          icon={CheckSquare} 
          color="rose"
        />
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Users by Role</p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {metrics.users_by_role && Object.entries(metrics.users_by_role).map(([role, count]) => (
                    <Badge 
                      key={role} 
                      className={`${roleBadgeColors[role] || 'bg-slate-500'} text-white`}
                    >
                      {roleLabels[role] || role}: {count}
                    </Badge>
                  ))}
                  {(!metrics.users_by_role || Object.keys(metrics.users_by_role).length === 0) && (
                    <span className="text-slate-400 text-sm">No users</span>
                  )}
                </div>
              </div>
              <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-slate-100 text-slate-700">
                <Shield className="h-6 w-6" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Activity Trend</p>
                <p className="text-2xl font-bold text-slate-900">
                  {((metrics.recent_contacts_30d || 0) + (metrics.recent_deals_30d || 0))}
                </p>
                <p className="text-xs text-slate-400 mt-1">New items in 30 days</p>
              </div>
              <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-green-100 text-green-700">
                <TrendingUp className="h-6 w-6" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sales Teams (embedded from OrganizationStructure so admins can see
          and manage this org's teams without leaving the dashboard) */}
      <Card className="mb-6">
        <CardHeader className="border-b">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Users size={20} />
                Teams ({teams.length})
              </CardTitle>
              <CardDescription>
                Teams inside {organization.name} — expand a row to see its agents
              </CardDescription>
            </div>
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => navigate('/team')}
              data-testid="manage-teams-btn"
            >
              <Settings size={16} />
              Manage Teams
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-4 space-y-3">
          {teams.length === 0 ? (
            <div className="text-center py-8 bg-slate-50 rounded-lg border border-dashed border-slate-200">
              <Users className="mx-auto text-slate-300 mb-2" size={32} />
              <p className="text-slate-500">No teams in this organization yet</p>
              <Button
                variant="link"
                className="text-blue-600"
                onClick={() => navigate('/team')}
              >
                Go to Team Management
              </Button>
            </div>
          ) : (
            teams.map((team) => {
              const members = getUsersForTeam(team.id);
              const memberCount = team.member_count ?? members.length;
              return (
                <div key={team.id} className="border rounded-lg overflow-hidden">
                  <div
                    className="flex items-center gap-3 p-3 bg-blue-50 cursor-pointer hover:bg-blue-100 transition-colors"
                    onClick={() => toggleTeam(team.id)}
                  >
                    {expandedTeams[team.id] ? (
                      <ChevronDown size={18} className="text-blue-600" />
                    ) : (
                      <ChevronRight size={18} className="text-blue-600" />
                    )}
                    <div className="p-2 bg-blue-500 rounded-lg">
                      <Users size={16} className="text-white" />
                    </div>
                    <div className="flex-1">
                      <span className="font-medium text-slate-800">{team.name}</span>
                      <span className="text-sm text-slate-500 ml-2">
                        ({memberCount} agents)
                      </span>
                    </div>
                    <Badge
                      variant="outline"
                      className={`mr-2 ${teamTypeColors[team.team_type] || 'text-slate-600 border-slate-200'}`}
                    >
                      {team.team_type_info?.name
                        || teamTypeLabels[team.team_type]
                        || team.team_type
                        || 'General'}
                    </Badge>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteTeam(team.id, team.name);
                      }}
                      className="text-xs px-2 py-1 rounded hover:bg-red-100 text-slate-500 hover:text-red-600 transition-colors"
                      data-testid={`delete-team-${team.id}`}
                    >
                      Delete
                    </button>
                  </div>
                  {expandedTeams[team.id] && (
                    <div className="border-t bg-white">
                      {members.length === 0 ? (
                        <div className="p-4 text-center text-slate-500 text-sm">
                          No agents assigned to this team
                        </div>
                      ) : (
                        <div className="divide-y">
                          {members.map((m) => (
                            <div key={m.id} className="flex items-center gap-3 p-3 hover:bg-slate-50">
                              <div className="w-6" />
                              <div className="w-8 h-8 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-xs font-semibold">
                                {m.full_name?.[0]?.toUpperCase() || m.email?.[0]?.toUpperCase() || 'U'}
                              </div>
                              <div className="flex-1">
                                <div className="text-sm font-medium text-slate-800">{m.full_name}</div>
                                <div className="text-xs text-slate-500">{m.email}</div>
                              </div>
                              <Badge className={`${roleBadgeColors[m.role] || 'bg-slate-500'} text-white text-xs`}>
                                {roleLabels[m.role] || m.role}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="p-2 bg-slate-50 border-t">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-full text-blue-600"
                          onClick={(e) => openAddAgentDialog(team, e)}
                          data-testid={`add-agent-btn-${team.id}`}
                        >
                          <UserPlus size={14} className="mr-1" />
                          Add Agent to Team
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardHeader className="border-b">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Users size={20} />
                Organization Users
              </CardTitle>
              <CardDescription>{users.length} user(s) in this organization</CardDescription>
            </div>
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => navigate('/users')}
            >
              <UserPlus size={16} />
              Manage Users
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-semibold">USER</TableHead>
                <TableHead className="font-semibold">EMAIL</TableHead>
                <TableHead className="font-semibold">ROLE</TableHead>
                <TableHead className="font-semibold">STATUS</TableHead>
                <TableHead className="font-semibold">JOINED</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                    No users in this organization yet
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-sm font-semibold">
                          {user.full_name?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase() || 'U'}
                        </div>
                        <span className="font-medium text-slate-800">{user.full_name || 'Unknown'}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-slate-600">
                        <Mail size={14} className="text-slate-400" />
                        {user.email}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${roleBadgeColors[user.role] || 'bg-slate-500'} text-white`}>
                        {roleLabels[user.role] || user.role}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {user.is_disabled ? (
                        <Badge className="bg-red-100 text-red-700">Disabled</Badge>
                      ) : (
                        <Badge className="bg-green-100 text-green-700">Active</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-slate-500 text-sm">
                      {formatDate(user.created_at)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add Team dialog removed — team creation now lives in the
          dedicated Team Management page (/team), reachable from the
          "Manage Teams" button above. Keeps this dashboard focused on
          the read-only org snapshot. */}

      {/* Add Agent to Team dialog — picks an existing org user; the backend
          moves them to this team (transferring from any other team). */}
      <Dialog open={!!addAgentTeam} onOpenChange={(open) => !open && setAddAgentTeam(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Agent to {addAgentTeam?.name}</DialogTitle>
            <DialogDescription>
              Pick a user from {organization?.name}. If they're already on
              another team they'll be moved.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label>Choose user</Label>
            <Select
              value={addAgentUserId}
              onValueChange={setAddAgentUserId}
            >
              <SelectTrigger data-testid="add-agent-user-select">
                <SelectValue placeholder="Select a user…" />
              </SelectTrigger>
              <SelectContent>
                {getAssignableUsers(addAgentTeam?.id).length === 0 ? (
                  <div className="px-3 py-2 text-sm text-slate-500">
                    Everyone in this org is already on this team.
                  </div>
                ) : (
                  getAssignableUsers(addAgentTeam?.id).map((u) => {
                    const onOtherTeam = u.team_id && u.team_id !== addAgentTeam?.id;
                    const otherTeamName = onOtherTeam
                      ? teams.find((t) => t.id === u.team_id)?.name
                      : null;
                    return (
                      <SelectItem key={u.id} value={u.id}>
                        {u.full_name || u.email}
                        {onOtherTeam && (
                          <span className="text-xs text-slate-500 ml-2">
                            (currently in {otherTeamName || 'another team'})
                          </span>
                        )}
                      </SelectItem>
                    );
                  })
                )}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddAgentTeam(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleAddAgentToTeam}
              disabled={savingAgent || !addAgentUserId}
              data-testid="add-agent-submit"
            >
              {savingAgent ? 'Adding…' : 'Add to Team'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrganizationDashboard;
